import type {
    ResponseRequest, SidebarDetails, UploadIconResponse, Details, ApiBody, UpdateApiResponse,
    ResponseCareerHistoriesOptions, ResponsePrefecture, RecommendInterests,
    ResponseRecommendSkill, RecommendBrands, HistoriesCounts, Histories,
    ResponseShowUncompletedProfiles, ResponsePreferenceConditions
} from '../types/personShow';
import { wrapFetch } from '../libs/fetch';

export const fetchSidebarDetails = async (personId: number) => {
  return await wrapFetch<SidebarDetails>(`/api/v1/person_profiles/show_sidebar?id=${personId}`);
};

export const uploadIcon = async (file: File, csrfToken: string) => {
  const formData = new FormData();
  formData.append('file', file);

  return await wrapFetch<UploadIconResponse>('/api/v1/person_profiles/upload_icon', {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken,
    },
    body: formData,
    credentials: 'same-origin',
  });
};

export const fetchDetails = async (personId: number) => {
  return await wrapFetch<Details>(`/api/v1/person_profiles/show_main?id=${personId}`);
};

export const updateApi = async (personId: number, body: ApiBody, csrfToken: string) => {
  return await wrapFetch<UpdateApiResponse>(`/api/v1/person_profiles/${personId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  });
};

export const sendRequest = async (personId: number, csrfToken: string) => {
  return await wrapFetch<ResponseRequest>(`/api/v1/person_profiles/profile/${personId}/request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    credentials: 'same-origin',
  });
};

export const fetchPersonHistories = async (personId: number) => {
  return await wrapFetch<Histories>(`/api/v1/person_profiles/show_histories?id=${personId}`);
};

export const fetchRecommendSkills = async () => {
  return await wrapFetch<ResponseRecommendSkill>('/api/v1/person_profiles/recommend_skill');
};

// 職種・年収のマスタはユーザーに依存しないため、ページ表示中は1回の取得結果を共有する。
// 同時呼び出しは実行中のPromiseを共有し、失敗時は破棄して次回呼び出しで再取得できるようにする。
// 利用側は返却値を直接変更しないこと（全呼び出し元で共有される）。
let occupationsCache: Promise<ResponseCareerHistoriesOptions> | null = null;

export const fetchOccupations = (): Promise<ResponseCareerHistoriesOptions> => {
  if (!occupationsCache) {
    occupationsCache = wrapFetch<ResponseCareerHistoriesOptions>('/api/v1/person_profiles/get_career_histories_options')
      .then((response) => {
        if (!Array.isArray(response?.suggestOccupations)) {
          throw new Error('Invalid career histories options response');
        }
        return response;
      })
      .catch((error) => {
        occupationsCache = null;
        throw error;
      });
  }
  return occupationsCache;
};

export const resetOccupationsCacheForTest = () => {
  occupationsCache = null;
};

export const fetchPreferenceConditions = async () => {
  return await wrapFetch<ResponsePreferenceConditions>('/api/v1/person_profiles/get_preference_conditions');
};

export const fetchPrefectures = async () => {
  return await wrapFetch<ResponsePrefecture>('/api/v1/person_profiles/get_prefectures');
};

export const fetchRecommendInterests = async () => {
  return await wrapFetch<RecommendInterests>('/api/v1/person_profiles/recommend_interest');
};

export const fetchRecommendBrands = async (target: string) => {
  return await wrapFetch<RecommendBrands>(`/api/v1/person_profiles/search_brand?targetBrand=${target}`);
};

export const fetchHistoriesCounts = async (personId: number) => {
  return await wrapFetch<HistoriesCounts>(`/api/v1/person_profiles/show_histories_counts?id=${personId}`);
};

export const fetchUncompletedProfiles = async (personId: number) => {
  return await wrapFetch<ResponseShowUncompletedProfiles>(`/api/v1/person_profiles/show_uncompleted_profiles?id=${personId}`);
};

export type ApplyItem = {
  title: string;
  selection_status: string;
  processing_selection_status: string;
  is_status_locked: boolean;
  id: number;
  recruitment_id: number;
  created_at: string;
};

export type EventItem = {
  title: string;
  selection_status: string;
  id: number;
  created_at: string;
  cancel: string;
};

export type EntriesForCompanyResponse = {
  applies: ApplyItem[];
  events: EventItem[];
};

export const fetchEntriesForCompany = async (personId: number) => {
  return await wrapFetch<EntriesForCompanyResponse>(`/api/v1/person_profiles/show_entries_for_company?id=${personId}`);
};
